import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { type SxProps } from '@mui/system'
import { KNDeliveryPastDueIcon, KNPickupPastDueIcon } from 'components/KNIcon/KNMaterialIcon'
import { useTripsView } from 'context/TripsViewContext'
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PredefinedFiltersItem from './PredefinedFiltersItem'
import type { CountersData } from './TripDashboard.types'

export interface PredefinedFiltersProps {
  counters: CountersData
  sx?: SxProps
}

const PredefinedFilters = ({ counters, sx }: PredefinedFiltersProps): ReactElement => {
  const { t } = useTranslation()
  const { predefinedFilter, setPredefinedFilter } = useTripsView()

  const filters = useMemo(
    () => [
      {
        name: 'PICKUP_EXPECTED',
        label: t('screens.cs.trip_dashboard.predefined_filters.pickup_expected'),
        icon: <FileUploadIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.pickup_expected_tooltip'),
      },
      {
        name: 'DELIVERY_EXPECTED',
        label: t('screens.cs.trip_dashboard.predefined_filters.delivery_expected'),
        icon: <FileDownloadIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.delivery_expected_tooltip'),
      },
      {
        name: 'PICKUP_PAST_DUE',
        label: t('screens.cs.trip_dashboard.predefined_filters.pickup_past_due'),
        icon: <KNPickupPastDueIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.pickup_past_due_tooltip'),
      },
      {
        name: 'DELIVERY_PAST_DUE',
        label: t('screens.cs.trip_dashboard.predefined_filters.delivery_past_due'),
        icon: <KNDeliveryPastDueIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.delivery_past_due_tooltip'),
      },
      {
        name: 'POD_MISSING',
        label: t('screens.cs.trip_dashboard.predefined_filters.pod_missing'),
        icon: <InsertDriveFileOutlinedIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.pod_missing_tooltip'),
      },
      {
        name: 'VEHICLE_MISSING',
        label: t('screens.cs.trip_dashboard.predefined_filters.vehicle_missing'),
        icon: <LocalShippingOutlinedIcon />,
        tooltip: t('screens.cs.trip_dashboard.predefined_filters.vehicle_missing_tooltip'),
      },
    ],
    [],
  )

  return (
    <Box
      data-guide="predefined-filters"
      sx={{
        padding: 1,
        ...sx,
      }}
    >
      <Grid container spacing={{ xs: 1, md: 2 }} justifyContent="center">
        {filters.map((filter) => (
          <Grid size={{ xs: 6, sm: 4, md: 1 }} key={filter.name}>
            <PredefinedFiltersItem
              label={filter.label}
              icon={filter.icon}
              counter={counters[filter.name] ?? 0}
              tooltip={filter.tooltip}
              active={predefinedFilter === filter.name}
              onClick={() => {
                setPredefinedFilter(filter.name)
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default PredefinedFilters
